import { useMemo } from "react";
import { useTranslations } from "@/lib/useTranslations";

import { updateArticleTaxonomy } from "@/api/articles";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { buildArtisticPeriodSelectGroups } from "@/core/services/taxonomy.artistic-periods";
import type { ArticleTaxonomy } from "@/core/services/types/article.types";
import type {
  ArtForm,
  ArtisticPeriod,
  CulturalArea,
  ExhibitionType,
} from "@/core/services/types/taxonomy.types";
import { useRefresh } from "@/hooks/useRefresh";
import { useTaxonomyAutoSave } from "@/hooks/useTaxonomyAutoSave";

interface ArticleTaxonomyPanelProps {
  articleId: string;
  taxonomy: ArticleTaxonomy;
  artForms: ArtForm[];
  artisticPeriods: ArtisticPeriod[];
  culturalAreas: CulturalArea[];
  exhibitionTypes: ExhibitionType[];
}

function SaveSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

export function ArticleTaxonomyPanel({
  articleId,
  taxonomy,
  artForms,
  artisticPeriods,
  culturalAreas,
  exhibitionTypes,
}: ArticleTaxonomyPanelProps) {
  const t = useTranslations("articles.edit");
  const refresh = useRefresh();
  const {
    selection,
    setArtFormIds,
    setArtisticPeriodIds,
    setCulturalAreaIds,
    setExhibitionTypeIds,
    state,
    isPending,
  } = useTaxonomyAutoSave({
    entityId: articleId,
    taxonomy,
    updateAction: updateArticleTaxonomy,
    onSuccess: refresh,
  });

  const artFormOptions = useMemo(
    () => artForms.map((f) => ({ id: f.id, label: f.label })),
    [artForms],
  );
  const artisticPeriodGroups = useMemo(
    () => buildArtisticPeriodSelectGroups(artisticPeriods),
    [artisticPeriods],
  );
  const culturalAreaOptions = useMemo(
    () => culturalAreas.map((c) => ({ id: c.id, label: c.label })),
    [culturalAreas],
  );
  const exhibitionTypeOptions = useMemo(
    () => exhibitionTypes.map((item) => ({ id: item.id, label: item.label })),
    [exhibitionTypes],
  );

  return (
    <section className="wireframe-border bg-white p-5 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-whart-text">
          {t("taxonomyLabel")}
        </p>
        {isPending ? <SaveSpinner /> : null}
      </div>

      <div className="space-y-4">
        {artForms.length > 0 ? (
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-neutral-500">
              {t("taxonomyArtForms")}
            </label>
            <MultiSelect
              options={artFormOptions}
              value={selection.artFormIds}
              onChange={setArtFormIds}
              placeholder={t("taxonomySelectPlaceholder")}
              searchPlaceholder={t("taxonomySearchPlaceholder")}
              emptyMessage={t("taxonomyEmpty")}
              disabled={isPending}
            />
          </div>
        ) : null}

        {culturalAreas.length > 0 ? (
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-neutral-500">
              {t("taxonomyCulturalAreas")}
            </label>
            <MultiSelect
              options={culturalAreaOptions}
              value={selection.culturalAreaIds}
              onChange={setCulturalAreaIds}
              placeholder={t("taxonomySelectPlaceholder")}
              searchPlaceholder={t("taxonomySearchPlaceholder")}
              emptyMessage={t("taxonomyEmpty")}
              disabled={isPending}
            />
          </div>
        ) : null}

        {artisticPeriods.length > 0 ? (
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-neutral-500">
              {t("taxonomyArtisticPeriods")}
            </label>
            <MultiSelect
              groups={artisticPeriodGroups}
              value={selection.artisticPeriodIds}
              onChange={setArtisticPeriodIds}
              placeholder={t("taxonomySelectPlaceholder")}
              searchPlaceholder={t("taxonomySearchPlaceholder")}
              emptyMessage={t("taxonomyEmpty")}
              disabled={isPending}
            />
          </div>
        ) : null}

        {exhibitionTypes.length > 0 ? (
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-neutral-500">
              {t("taxonomyExhibitionTypes")}
            </label>
            <MultiSelect
              options={exhibitionTypeOptions}
              value={selection.exhibitionTypeIds}
              onChange={setExhibitionTypeIds}
              placeholder={t("taxonomySelectPlaceholder")}
              searchPlaceholder={t("taxonomySearchPlaceholder")}
              emptyMessage={t("taxonomyEmpty")}
              disabled={isPending}
            />
          </div>
        ) : null}

        {!state.success && state.error ? (
          <p role="alert" className="text-xs text-whart-urgent">
            {state.error}
          </p>
        ) : null}

        {state.success ? (
          <p role="status" className="text-xs text-emerald-700">
            {t("saveTaxonomySuccess")}
          </p>
        ) : null}
      </div>
    </section>
  );
}
